import { useCallback, useLayoutEffect, useRef, useState } from 'react';

const SCALE_EPSILON = 0.001;

const getParentContentWidth = (parent: HTMLElement) => {
  const styles = getComputedStyle(parent);
  const paddingLeft = parseFloat(styles.paddingLeft);
  const paddingRight = parseFloat(styles.paddingRight);

  return (
    parent.clientWidth -
    (Number.isFinite(paddingLeft) ? paddingLeft : 0) -
    (Number.isFinite(paddingRight) ? paddingRight : 0)
  );
};

export const useAutoScale = () => {
  const parentRef = useRef<HTMLElement | null>(null);
  const [el, setEl] = useState<HTMLElement | null>(null);

  const elementRef = useCallback((node: HTMLElement | null) => {
    setEl(node);
  }, []);

  useLayoutEffect(() => {
    if (!el) return;

    const parent = el.parentElement;
    parentRef.current = parent;

    if (!parent) return;

    let rafId = 0;
    let secondRafId = 0;
    let lastTransform = el.style.transform;
    let lastOverflowX = parent.style.overflowX;

    const applyScale = () => {
      parentRef.current = el.parentElement;

      const p = el.parentElement;
      if (!p) return;

      const elementWidth = el.offsetWidth;
      const parentWidth = getParentContentWidth(p);

      if (
        !elementWidth ||
        !parentWidth ||
        !Number.isFinite(elementWidth) ||
        !Number.isFinite(parentWidth)
      ) {
        return;
      }

      const scale = Math.min(1, parentWidth / elementWidth);
      const nextTransform = scale < 1 - SCALE_EPSILON ? `scale(${scale})` : '';
      const nextOverflowX = nextTransform ? 'hidden' : '';

      if (el.style.transformOrigin !== 'top left') {
        el.style.transformOrigin = 'top left';
      }

      if (lastTransform !== nextTransform) {
        el.style.transform = nextTransform;
        lastTransform = nextTransform;
      }

      if (lastOverflowX !== nextOverflowX) {
        p.style.overflowX = nextOverflowX;
        lastOverflowX = nextOverflowX;
      }
    };

    const scheduleScale = () => {
      cancelAnimationFrame(rafId);
      cancelAnimationFrame(secondRafId);

      rafId = requestAnimationFrame(() => {
        secondRafId = requestAnimationFrame(applyScale);
      });
    };

    scheduleScale();

    const ro = new ResizeObserver(scheduleScale);
    ro.observe(el);
    ro.observe(parent);
    window.addEventListener('resize', scheduleScale);

    return () => {
      cancelAnimationFrame(rafId);
      cancelAnimationFrame(secondRafId);
      ro.disconnect();
      window.removeEventListener('resize', scheduleScale);
      el.style.transform = '';
      parent.style.overflowX = '';
    };
  }, [el]);

  return elementRef;
};
